'use client';

import { useAppointmentReasonOptions } from '@/hooks/useAppointmentReasonOptions';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { getTitleOptions } from '@/lib/forms/titleOptions';
import { getCountryFromLocale } from '@/lib/i18n/config';
import { getPhonePrefixSelectOptByCode } from '@/lib/phonePrefixes/helpers';
import {
  getAvailableTimeSlots,
  getNextAvailableDate,
  type TimeSlotOption,
} from '@/lib/stores/appointmentScheduling';
import { devError } from '@/lib/utils/devLog';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';

import { getUserFromClientCookie } from '@/lib/auth/clientUser';

import type { SelectedBoutique } from './types';
import {
  buildAppointmentFormSchema,
  getAppointmentFormDefaults,
  submitAppointmentForm,
} from './utils';

export type AppointmentFormData = {
  title: string;
  firstName: string;
  lastName: string;
  firstNameKanji: string;
  lastNameKanji: string;
  email: string;
  phonePrefix: string;
  phone: string;
  reason: string;
  timeSlot: string;
  comments: string;
  newsletterOptIn: boolean;
  consent: boolean;
};

export interface UseAppointmentFormStepOptions {
  boutique: SelectedBoutique | null;
  productSku?: string;
  countryCode: string;
  localeSlug: string;
  languageCode: string;
  layout?: 'modal' | 'page';
  onSuccess: (fullName: string) => void;
}

function resetCaptchaWidget(
  recaptchaRef: RefObject<ReCAPTCHA | null>,
  setCaptchaToken: (token: string | null) => void
) {
  recaptchaRef.current?.reset();
  setCaptchaToken(null);
}

/**
 * Form state, validation, and submit logic for the appointment booking step.
 *
 * Example Input:
 * boutique, countryCode="US", languageCode="en", onSuccess callback
 *
 * Example Output:
 * { register, onSubmit, selectedDate, isPageLayout, ... }
 */
export function useAppointmentFormStep({
  boutique,
  productSku,
  countryCode,
  localeSlug: _localeSlug,
  languageCode,
  layout = 'modal',
  onSuccess,
}: UseAppointmentFormStepOptions) {
  const t = useTranslations('appointment.form');
  const { sendGenerateLead } = useGoogleAnalytics();
  const tForms = useTranslations('forms');
  const locale = useLocale();
  const marketCountry = getCountryFromLocale(locale).toUpperCase();
  const { options: reasonOptions, defaultOption } = useAppointmentReasonOptions();
  const isPageLayout = layout === 'page';
  const schedulingDisabled = !boutique;

  const today = useMemo(() => new Date(), []);
  const tomorrow = useMemo(() => {
    const date = new Date(today);
    date.setDate(today.getDate() + 1);
    return date;
  }, [today]);
  const futureDate = useMemo(() => {
    const date = new Date(today);
    date.setDate(today.getDate() + 30);
    return date;
  }, [today]);

  const initialDate = useMemo(
    () =>
      getNextAvailableDate(tomorrow, boutique?.unavailableWeekdays ?? []) ||
      dayjs(tomorrow).format('YYYY-MM-DD'),
    [boutique?.unavailableWeekdays, tomorrow]
  );

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [availableTimes, setAvailableTimes] = useState<TimeSlotOption[]>([]);
  const [nonWorkingDays, setNonWorkingDays] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const defaultPhonePrefix = useMemo(
    () => getPhonePrefixSelectOptByCode(countryCode)?.code ?? 'US',
    [countryCode]
  );

  const schema = useMemo(
    () => buildAppointmentFormSchema(t, tForms, marketCountry),
    [t, tForms, marketCountry]
  );

  const [loggedInUser] = useState(() => getUserFromClientCookie());
  const titleOptions = useMemo(
    () => getTitleOptions(marketCountry, (key) => tForms(key)),
    [marketCountry, tForms]
  );

  const formDefaultValues = useMemo(
    () =>
      getAppointmentFormDefaults(loggedInUser, {
        title: titleOptions[0]?.value,
        phonePrefix: defaultPhonePrefix,
        reason: defaultOption.value,
        titleOptions,
        countryCode: marketCountry,
      }),
    [loggedInUser, titleOptions, defaultPhonePrefix, defaultOption.value, marketCountry]
  );

  const formMethods = useForm<AppointmentFormData>({
    resolver: zodResolver(schema),
    defaultValues: formDefaultValues,
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = formMethods;

  const refreshTimeSlots = useCallback(
    (date: string) => {
      if (!boutique) {
        setAvailableTimes([]);
        setNonWorkingDays([]);
        setValue('timeSlot', '', { shouldValidate: false });
        return;
      }

      const { times, nonWorkingDays: holidayClosedDays } = getAvailableTimeSlots(
        boutique.appointments,
        date,
        boutique.holidayHours,
        boutique.reopenDate
      );
      setAvailableTimes(times);
      setNonWorkingDays(holidayClosedDays);
      setValue('timeSlot', times[0]?.value ?? '', { shouldValidate: true });
    },
    [boutique, setValue]
  );

  useEffect(() => {
    setSelectedDate(initialDate);
  }, [initialDate, boutique?.locationId]);

  useEffect(() => {
    refreshTimeSlots(selectedDate);
  }, [selectedDate, refreshTimeSlots]);

  const handleDateChange = useCallback((date: Date) => {
    setSelectedDate(dayjs(date).format('YYYY-MM-DD'));
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    if (!boutique) {
      setSubmitError(t('boutiqueRequired'));
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const result = await submitAppointmentForm({
        boutique,
        values,
        selectedDate,
        countryCode,
        languageCode,
        productSku,
        captchaToken,
        titleOptions,
        translate: t,
        translateForms: tForms,
      });

      if (result.ok) {
        void sendGenerateLead({
          appointmentMotive: result.appointmentMotive,
          appointmentId: result.appointmentId,
          storeId: result.storeId,
          dateAppointment: result.dateAppointment,
          sku: result.productSku,
        });
        onSuccess(result.fullName);
        return;
      }

      setSubmitError(result.error);
      if (result.resetCaptcha) {
        resetCaptchaWidget(recaptchaRef, setCaptchaToken);
      }
    } catch (error) {
      devError('[AppointmentFormStep] Submit failed:', error);
      setSubmitError(t('submitError'));
      resetCaptchaWidget(recaptchaRef, setCaptchaToken);
    } finally {
      setIsSubmitting(false);
    }
  });

  return {
    t,
    tForms,
    reasonOptions,
    isPageLayout,
    schedulingDisabled,
    tomorrow,
    futureDate,
    selectedDate,
    availableTimes,
    nonWorkingDays,
    submitError,
    isSubmitting,
    captchaToken,
    setCaptchaToken,
    recaptchaRef,
    titleOptions,
    formMethods,
    register,
    onSubmit,
    handleDateChange,
    errors,
    layout,
    boutique,
  };
}

export type AppointmentFormStepState = ReturnType<typeof useAppointmentFormStep>;
